import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { ChatDock } from '../assistant/chat-dock';
import { PortalContainerProvider } from '../components/portal-container';
import type { DockPosition } from '../assistant/types';
import css from '../styles.css?inline';

export class AssistantDockElement extends HTMLElement {
  // `title-text`, not `title`: a `title` attribute on any HTML element renders
  // as a browser tooltip on hover, which is not what this is.
  static observedAttributes = [
    'chat-url',
    'conversations-url',
    'csrf-token',
    'position',
    'offset',
    'width',
    'height',
    'resizable',
    'title-text',
    'badge',
    'launcher-label',
    'launcher-style',
    'launcher-icon',
    'accent',
    'open-on-load',
    'greeting',
    'greeting-subtitle',
    'starters',
    'disclaimer',
    'stream',
    'feedback',
    'max-messages',
    'max-message-chars',
    'max-attachments-per-message',
    'max-attachment-bytes',
    'allowed-attachment-types',
    'package-version',
  ];

  /** Kept only as the fallback when the Blade partial's attribute is absent
   *  (e.g. a hand-written element). The real default lives in
   *  config/assistant.php's allowed_attachment_mime_types. */
  private static readonly DEFAULT_ATTACHMENT_TYPES = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
  ];

  private root?: Root;
  private mountPoint?: HTMLDivElement;

  connectedCallback() {
    if (this.shadowRoot) return;
    const shadow = this.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = css;
    shadow.appendChild(style);
    this.mountPoint = document.createElement('div');
    shadow.appendChild(this.mountPoint);
    this.root = createRoot(this.mountPoint);
    this.render();
  }

  attributeChangedCallback() {
    if (this.root) this.render();
  }

  disconnectedCallback() {
    this.root?.unmount();
    this.root = undefined;
  }

  private boolAttr(name: string, fallback: boolean): boolean {
    const raw = this.getAttribute(name);
    if (raw === null) return fallback;
    return raw !== 'false' && raw !== '0' && raw !== '';
  }

  private numAttr(name: string, fallback: number): number {
    const raw = this.getAttribute(name);
    if (raw === null) return fallback;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  }

  /** Like numAttr, but 0 is a valid value (used to turn attachments off). */
  private nonNegativeIntAttr(name: string, fallback: number): number {
    const raw = this.getAttribute(name);
    if (raw === null) return fallback;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : fallback;
  }

  /**
   * The Blade partial already validates this, but the element can also be
   * hand-written into a page — an unrecognised value would match no anchor
   * branch and leave the dock unpositioned, so it is checked here too.
   */
  /**
   * Starter prompts arrive as a JSON array attribute. Anything malformed
   * degrades to no starters rather than throwing — an empty state with no
   * suggestions is still a usable chat, a crashed element is not.
   */
  private resolveStarters(): string[] {
    const raw = this.getAttribute('starters');
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((item): item is string => typeof item === 'string' && item.trim() !== '').slice(0, 4);
    } catch {
      return [];
    }
  }

  private resolvePosition(): DockPosition {
    const raw = this.getAttribute('position');
    const allowed: DockPosition[] = ['bottom-right', 'bottom-left', 'top-right', 'top-left'];
    return allowed.includes(raw as DockPosition) ? (raw as DockPosition) : 'bottom-right';
  }

  /** Comma-separated mime list, e.g. "image/jpeg,image/png,application/pdf". */
  private resolveAllowedAttachmentTypes(): string[] {
    const raw = this.getAttribute('allowed-attachment-types');
    if (!raw) return AssistantDockElement.DEFAULT_ATTACHMENT_TYPES;

    const types = raw
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t !== '');

    return types.length > 0 ? types : AssistantDockElement.DEFAULT_ATTACHMENT_TYPES;
  }

  private render() {
    if (!this.root || !this.mountPoint) return;
    this.root.render(
      React.createElement(
        PortalContainerProvider,
        { container: this.mountPoint },
        React.createElement(ChatDock, {
          config: {
            chatUrl: this.getAttribute('chat-url') ?? '',
            conversationsUrl: this.getAttribute('conversations-url') ?? '',
            healthUrl: this.getAttribute('health-url') ?? '',
            csrfToken: this.getAttribute('csrf-token') ?? '',
            position: this.resolvePosition(),
            offset: Math.min(80, Math.max(0, this.numAttr('offset', 20))),
            width: this.numAttr('width', 404),
            height: this.numAttr('height', 580),
            resizable: this.boolAttr('resizable', true),
            title: this.getAttribute('title-text') ?? 'Document assistant',
            badge: (this.getAttribute('badge') ?? '').trim(),
            launcherLabel: this.getAttribute('launcher-label') ?? 'Ask about documents',
            launcherStyle: this.getAttribute('launcher-style') === 'icon' ? 'icon' : 'label',
            launcherIcon: this.getAttribute('launcher-icon') ?? '',
            accent: this.getAttribute('accent') ?? '#1f6f6a',
            openOnLoad: this.boolAttr('open-on-load', false),
            greeting: this.getAttribute('greeting') ?? 'What can I help you find?',
            greetingSubtitle: this.getAttribute('greeting-subtitle') ?? '',
            starters: this.resolveStarters(),
            disclaimer: this.getAttribute('disclaimer') ?? '',
            stream: this.boolAttr('stream', true),
            feedback: this.boolAttr('feedback', false),
            maxMessages: this.numAttr('max-messages', 20),
            maxMessageChars: this.numAttr('max-message-chars', 8000),
            maxAttachmentsPerMessage: this.nonNegativeIntAttr('max-attachments-per-message', 4),
            maxAttachmentBytes: this.numAttr('max-attachment-bytes', 8 * 1024 * 1024),
            allowedAttachmentTypes: this.resolveAllowedAttachmentTypes(),
            packageVersion: this.getAttribute('package-version') ?? '',
          },
        }),
      ),
    );
  }
}
